/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Icons, Positions, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal } from '../style';
import { Col, FormRow } from 'src/UI/Grid';
import Button from 'src/UI/Button';
import ErrorMessage from 'src/UI/ErrorMessage';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import { get, post } from 'src/services/GenesisApi';
import { sendNotification } from 'src/UI/Notification';
import {
  GET_ORGANIZATIONS,
  MAX_LENGTH_20,
  MAX_LENGTH_50,
  MESSAGES,
  ParameterScope,
  SET_NOMINATION_GROUP,
} from 'src/constants/EUMarkets';
import {
  IAddEditNominationGroup,
  ILocationParameterDto,
  IOrganization,
  INGParameters,
} from 'src/types/EUMarkets.type';
import { ISelectOptions } from 'src/types/Common.type';
import { handleError } from 'src/utils/common/utils';
import {
  formatDateOnly,
  hasOverlappingParameterDates,
  isDateRangeValid,
} from '../../Locations/AddEditParameter';
import InlineParameterList, {
  getParameterKey,
} from '../../Locations/AddEditParameter/InlineParameterList';

const { Add: AddIcon } = Icons;

const INTERNAL_ROLE_NAME = 'Internal';
const TRADING_PARTNER_ROLE_NAME = 'Trading Partner';

const hasRole = (organization: IOrganization, roleName: string) =>
  organization.roles?.some((role) => role.roleName === roleName);

const toOrganizationOption = (organization: IOrganization) => ({
  id: organization.id,
  name: organization.organizationName,
});

const defaultValues = {
  nominationGroupName: '',
  aliasName: '',
  internalOrganizationId: null,
  externalOrganizationId: null,
  validFrom: dayjs(),
  validTo: null,
};

const toParameterDto = (parameter: INGParameters): ILocationParameterDto => ({
  parameterDefinitionId: parameter.parameterDefinitionId,
  valueString: parameter.valueString,
  valueLong: parameter.valueLong,
  valueDouble: parameter.valueDouble,
  valueBoolean: parameter.valueBoolean,
  valueListId: parameter.valueListId ?? null,
  valueListValue: parameter.valueListValue ?? null,
  validFrom: parameter.validFrom,
  validTo: parameter.validTo,
});

const areParameterDatesWithinNominationGroupRange = (
  parameter: INGParameters,
  groupValidFrom: Dayjs | undefined,
  groupValidTo: Dayjs | null,
) => {
  if (!groupValidFrom?.isValid()) {
    return true;
  }

  const parameterValidFrom = dayjs(parameter.validFrom);
  if (!parameterValidFrom.isValid() || parameterValidFrom.isBefore(groupValidFrom, 'day')) {
    return false;
  }

  if (!groupValidTo?.isValid()) {
    return true;
  }

  const parameterValidTo = parameter.validTo ? dayjs(parameter.validTo) : null;
  return Boolean(parameterValidTo?.isValid() && !parameterValidTo.isAfter(groupValidTo, 'day'));
};

const removeAndReindexParameterRecord = <T,>(records: Record<number, T>, parameterIndex: number) =>
  Object.entries(records).reduce<Record<number, T>>((next, [key, value]) => {
    const index = Number(key);
    if (index === parameterIndex) {
      return next;
    }

    next[index > parameterIndex ? index - 1 : index] = value;
    return next;
  }, {});

const AddEditNominationGroup: FC<IAddEditNominationGroup> = ({
  open = false,
  onClose = () => {},
  nominationGroup = null,
  refreshData = () => {},
}) => {
  const methods = useForm();
  const { reset, watch, setValue, setError, clearErrors } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [internalOrganizations, setInternalOrganizations] = useState<ISelectOptions[]>([]);
  const [externalOrganizations, setExternalOrganizations] = useState<ISelectOptions[]>([]);
  const [parameters, setParameters] = useState<INGParameters[]>([]);
  const [parameterDrafts, setParameterDrafts] = useState<Record<number, INGParameters>>({});
  const [parameterDraftErrors, setParameterDraftErrors] = useState<Record<number, string>>({});
  const [pendingParameterCount, setPendingParameterCount] = useState(0);
  const validFrom = watch?.('validFrom');
  const validTo = watch?.('validTo');
  const parametersErrorMessage = methods.formState?.errors?.parameters?.message as
    string | undefined;

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoadingOrganizations(true);
    get(GET_ORGANIZATIONS)
      .then((response) => {
        const allOrganizations = (response.data || []) as IOrganization[];
        setInternalOrganizations(
          allOrganizations
            .filter((organization) => hasRole(organization, INTERNAL_ROLE_NAME))
            .map(toOrganizationOption),
        );
        setExternalOrganizations(
          allOrganizations
            .filter((organization) => hasRole(organization, TRADING_PARTNER_ROLE_NAME))
            .map(toOrganizationOption),
        );
      })
      .catch((error) => handleError(error, MESSAGES.SERVER_ERROR))
      .finally(() => setLoadingOrganizations(false));
  }, [open]);

  useEffect(() => {
    if (nominationGroup) {
      reset({
        nominationGroupName: nominationGroup.nominationGroupName,
        aliasName: nominationGroup.aliasName,
        internalOrganizationId: nominationGroup.internalOrganizationId,
        externalOrganizationId: nominationGroup.externalOrganizationId,
        validFrom: nominationGroup.validFrom ? dayjs(nominationGroup.validFrom) : undefined,
        validTo: nominationGroup.validTo ? dayjs(nominationGroup.validTo) : null,
      });
      setParameters(nominationGroup.parameters);
      setParameterDrafts({});
      setParameterDraftErrors({});
      setPendingParameterCount(0);
      return;
    }
    reset(defaultValues);
    setParameters([]);
    setParameterDrafts({});
    setParameterDraftErrors({});
    setPendingParameterCount(0);
  }, [nominationGroup, open, reset]);

  const handleClose = () => {
    reset(defaultValues);
    setParameters([]);
    setParameterDrafts({});
    setParameterDraftErrors({});
    setPendingParameterCount(0);
    clearErrors('parameters');
    onClose();
  };

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  // Refreshes the banner only once one is showing, so newly added rows don't error before a save.
  const syncParametersError = useCallback(
    (nextErrors: Record<number, string>) => {
      const firstError = Object.values(nextErrors).find(Boolean);
      if (!firstError) {
        clearErrors('parameters');
        return;
      }
      if (parametersErrorMessage) {
        setError('parameters', { type: 'validate', message: firstError });
      }
    },
    [clearErrors, setError, parametersErrorMessage],
  );

  const handleParameterDraftChange = useCallback(
    (parameter: INGParameters, rowIndex: number, error?: string) => {
      setParameterDrafts((currentDrafts) => ({ ...currentDrafts, [rowIndex]: parameter }));
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = { ...currentErrors, [rowIndex]: error ?? '' };
        syncParametersError(nextErrors);
        return nextErrors;
      });
    },
    [syncParametersError],
  );

  const handleParameterRemove = useCallback(
    (parameterIndex: number) => {
      setParameters((currentParameters) =>
        currentParameters.filter((_, index) => index !== parameterIndex),
      );
      setParameterDrafts((currentDrafts) =>
        removeAndReindexParameterRecord(currentDrafts, parameterIndex),
      );
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = removeAndReindexParameterRecord(currentErrors, parameterIndex);
        if (!Object.values(nextErrors).some(Boolean)) {
          clearErrors('parameters');
        }
        return nextErrors;
      });
    },
    [clearErrors],
  );

  const handleCancelParameterAdd = useCallback(
    (parameterIndex?: number) => {
      setPendingParameterCount((currentCount) => Math.max(0, currentCount - 1));
      if (parameterIndex !== undefined) {
        setParameterDrafts((currentDrafts) =>
          removeAndReindexParameterRecord(currentDrafts, parameterIndex),
        );
        setParameterDraftErrors((currentErrors) => {
          const nextErrors = removeAndReindexParameterRecord(currentErrors, parameterIndex);
          if (!Object.values(nextErrors).some(Boolean)) {
            clearErrors('parameters');
          }
          return nextErrors;
        });
      }
    },
    [clearErrors],
  );

  const handleSubmit = (formData) => {
    const pendingParameterIndexes = Array.from(
      { length: pendingParameterCount },
      (_, index) => parameters.length + index,
    );
    const mergedParametersRaw = [
      ...parameters.map((parameter, index) => parameterDrafts[index] ?? parameter),
      ...pendingParameterIndexes
        .map((index) => parameterDrafts[index])
        .filter((parameter): parameter is INGParameters => Boolean(parameter)),
    ];
    const mergedParameters = mergedParametersRaw.filter(
      (parameter, index, allParameters) =>
        allParameters.findIndex(
          (candidate) => getParameterKey(candidate) === getParameterKey(parameter),
        ) === index,
    );
    const parameterValidationError = Object.values(parameterDraftErrors).find(Boolean);
    if (parameterValidationError) {
      setError('parameters', {
        type: 'validate',
        message: parameterValidationError,
      });
      return;
    }
    if (pendingParameterIndexes.some((index) => !parameterDrafts[index])) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.REQUIRED_PARAMETER_DEFINITION,
      });
      return;
    }
    const groupValidFrom = formData.validFrom as Dayjs | undefined;
    const groupValidTo = (formData.validTo as Dayjs | null) ?? null;
    if (
      mergedParametersRaw.some(
        (parameter) =>
          !areParameterDatesWithinNominationGroupRange(parameter, groupValidFrom, groupValidTo),
      )
    ) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.PARAMETER_DATE_OUTSIDE_NOMINATION_GROUP_RANGE,
      });
      return;
    }
    if (hasOverlappingParameterDates(mergedParametersRaw)) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.OVERLAPPING_PARAMETER_DATE_RANGE,
      });
      return;
    }
    clearErrors('parameters');
    setLoading(true);
    const isUpdate = Boolean(nominationGroup);
    post(SET_NOMINATION_GROUP, {
      id: nominationGroup?.id ?? 0,
      nominationGroupName: formData.nominationGroupName,
      aliasName: formData.aliasName,
      internalOrganizationId: Number(formData.internalOrganizationId),
      externalOrganizationId: Number(formData.externalOrganizationId),
      isActive: nominationGroup?.isActive ?? true,
      validFrom: formatDateOnly(formData.validFrom as Dayjs),
      validTo: formData.validTo ? formatDateOnly(formData.validTo as Dayjs) : null,
      parameters: mergedParameters.map(toParameterDto),
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: isUpdate ? MESSAGES.UPDATED_NOMINATION_GROUP : MESSAGES.CREATED_NOMINATION_GROUP,
        });
        refreshData();
        handleClose();
      })
      .catch((error) => {
        handleError(
          error,
          isUpdate
            ? MESSAGES.UPDATED_NOMINATION_GROUP_FAILED
            : MESSAGES.CREATED_NOMINATION_GROUP_FAILED,
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      className='parameter-edit-modal'
      title={`${nominationGroup ? 'Modify' : 'Add a new'} nomination group`}
      open={open}
      onClose={handleClose}
      maskClosable={false}
      isDraggable
      width={1200}
      footer={
        <div className='button-style'>
          <Button type='button' onClick={handleClose} variant='outlined' data-testid='btn-cancel'>
            Cancel
          </Button>
          <Button
            data-testid='submit-form'
            type='button'
            loading={loading}
            disabled={loading}
            onClick={methods.handleSubmit(handleSubmit)}
          >
            Save
          </Button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <FormRow className='modal-form-row'>
          <Col size={4}>
            <FormControlTextInput
              controllerProps={{
                name: 'nominationGroupName',
                rules: {
                  required: MESSAGES.REQUIRED_NOMINATION_GROUP_NAME,
                  maxLength: { value: MAX_LENGTH_50, message: MESSAGES.MAX_LENGTH_50 },
                },
              }}
              label='Nomination Group Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Nomination Group Name' }}
            />
          </Col>
          <Col size={4}>
            <FormControlTextInput
              controllerProps={{
                name: 'aliasName',
                rules: {
                  maxLength: { value: MAX_LENGTH_20, message: MESSAGES.MAX_LENGTH_20 },
                },
              }}
              label='Alias Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Alias Name' }}
            />
          </Col>
          <Col size={4}>
            <FormControlSelect
              methods={methods}
              label='Internal Organization'
              isLoading={loadingOrganizations}
              data={internalOrganizations}
              controllerProps={{
                name: 'internalOrganizationId',
                rules: { required: MESSAGES.REQUIRED_INTERNAL_ORGANIZATION },
              }}
            />
          </Col>
          <Col size={4}>
            <FormControlSelect
              methods={methods}
              label='External Organization'
              isLoading={loadingOrganizations}
              data={externalOrganizations}
              controllerProps={{
                name: 'externalOrganizationId',
                rules: { required: MESSAGES.REQUIRED_EXTERNAL_ORGANIZATION },
              }}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid From'
              controllerProps={{
                name: 'validFrom',
                rules: {
                  required: MESSAGES.REQUIRED_VALID_FROM,
                  validate: (value) =>
                    isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              handleOnChange={handleValidFromChange}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid To'
              controllerProps={{
                name: 'validTo',
                rules: {
                  validate: (value) =>
                    isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              inputProps={{
                disabledDate: (current: Dayjs) =>
                  validFrom ? current.isBefore(validFrom, 'day') : false,
              }}
            />
          </Col>
        </FormRow>
        <div className='group-details group-details-with-action'>
          <strong className='param-title'>Nomination Group Parameters</strong>
          <Button
            onClick={() => setPendingParameterCount((count) => count + 1)}
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
            data-testid='add-nomination-group-parameter'
          >
            Add Row
          </Button>
        </div>
        <div className='parameter-table'>
          <InlineParameterList
            open={open}
            scopeId={ParameterScope.NominationGroup}
            locationId={nominationGroup?.id ?? 0}
            parameters={parameters}
            isAdding={false}
            addingParameterCount={pendingParameterCount}
            onCancelAdd={handleCancelParameterAdd}
            onRemove={handleParameterRemove}
            onDraftChange={handleParameterDraftChange}
          />
          {parametersErrorMessage && <ErrorMessage>{parametersErrorMessage}</ErrorMessage>}
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AddEditNominationGroup;
